import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
}

// Satu "baris stok" bisa mewakili item tanpa varian ATAU satu varian dari item
// stockRow: { id (stock.id), menu_item_id, menu_variant_id | null,
//             itemName, itemEmoji, itemCode, variantName | null,
//             qty, low_threshold, unit, updated_at }

export default function Stok() {
  const [stockRows, setStockRows]   = useState([]);   // stok yang sudah ada
  const [uninitialized, setUninit]  = useState([]);   // item/varian yang belum punya stok
  const [logs, setLogs]             = useState([]);
  const [selected, setSelected]     = useState(null); // { id: stock.id, label }

  const [editId, setEditId]         = useState(null); // stock.id
  const [editVal, setEditVal]       = useState({ qty: 0, low_threshold: 5, unit: 'porsi' });

  const [adjId, setAdjId]           = useState(null); // stock.id
  const [adjMode, setAdjMode]       = useState('manual');
  const [adjDelta, setAdjDelta]     = useState('');
  const [adjReason, setAdjReason]   = useState('');

  const [initKey, setInitKey]       = useState(null); // key of uninitialized row
  const [initVal, setInitVal]       = useState({ qty: 0, low_threshold: 5, unit: 'porsi' });

  const [search, setSearch]         = useState('');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    // 1. Ambil semua menu_items beserta varian-nya
    const { data: menuData } = await supabase
      .from('menu_items')
      .select('id, name, emoji, category, code, menu_variants(id, name, sort_order)')
      .order('category')
      .order('name');

    // 2. Ambil semua stock records (termasuk menu_variant_id)
    const { data: stockData } = await supabase
      .from('stock')
      .select('*');

    const stocks = stockData || [];
    const items  = menuData  || [];

    // Bangun index stok: key = "itemId" atau "itemId__variantId"
    const stockIndex = {};
    stocks.forEach(s => {
      const key = s.menu_variant_id
        ? `${s.menu_item_id}__${s.menu_variant_id}`
        : s.menu_item_id;
      stockIndex[key] = s;
    });

    // Bangun daftar "slot stok" yang diharapkan dari katalog menu
    const expectedSlots = [];
    items.forEach(item => {
      const variants = item.menu_variants || [];
      if (variants.length === 0) {
        // Item tanpa varian → satu slot per item
        expectedSlots.push({
          key:             item.id,
          menu_item_id:    item.id,
          menu_variant_id: null,
          itemName:        item.name,
          itemEmoji:       item.emoji,
          itemCode:        item.code,
          variantName:     null,
        });
      } else {
        // Item dengan varian → satu slot per varian
        [...variants].sort((a, b) => a.sort_order - b.sort_order).forEach(v => {
          expectedSlots.push({
            key:             `${item.id}__${v.id}`,
            menu_item_id:    item.id,
            menu_variant_id: v.id,
            itemName:        item.name,
            itemEmoji:       item.emoji,
            itemCode:        item.code,
            variantName:     v.name,
          });
        });
      }
    });

    // Pisahkan: sudah ada stok vs belum
    const withStock = [];
    const without   = [];
    expectedSlots.forEach(slot => {
      const s = stockIndex[slot.key];
      if (s) {
        withStock.push({ ...slot, ...s, _stockId: s.id });
      } else {
        without.push(slot);
      }
    });

    setStockRows(withStock);
    setUninit(without);
  }

  async function loadLogs(stockRow) {
    let q = supabase
      .from('stock_log')
      .select('id, delta, reason, reference_id, created_at')
      .eq('menu_item_id', stockRow.menu_item_id);

    if (stockRow.menu_variant_id) {
      q = q.eq('menu_variant_id', stockRow.menu_variant_id);
    } else {
      q = q.is('menu_variant_id', null);
    }

    const { data } = await q.order('created_at', { ascending: false }).limit(30);
    setLogs(data || []);
  }

  // ── Inisialisasi stok ────────────────────────────────────────────
  async function handleInitStock(slot) {
    const now = new Date().toISOString();
    const payload = {
      menu_item_id:    slot.menu_item_id,
      menu_variant_id: slot.menu_variant_id || null,
      qty:             initVal.qty,
      low_threshold:   initVal.low_threshold,
      unit:            initVal.unit,
      updated_at:      now,
    };
    await supabase.from('stock').insert(payload);
    if (initVal.qty > 0) {
      await supabase.from('stock_log').insert({
        menu_item_id:    slot.menu_item_id,
        menu_variant_id: slot.menu_variant_id || null,
        delta:           initVal.qty,
        reason:          'Inisialisasi stok',
        created_at:      now,
      });
    }
    setInitKey(null);
    loadAll();
  }

  // ── Set manual ───────────────────────────────────────────────────
  async function handleSetStock(row) {
    const now   = new Date().toISOString();
    const delta = editVal.qty - row.qty;
    await supabase.from('stock').update({
      qty: editVal.qty, low_threshold: editVal.low_threshold, unit: editVal.unit, updated_at: now,
    }).eq('id', row._stockId);
    if (delta !== 0) {
      await supabase.from('stock_log').insert({
        menu_item_id:    row.menu_item_id,
        menu_variant_id: row.menu_variant_id || null,
        delta,
        reason:          'Set manual',
        created_at:      now,
      });
    }
    setEditId(null);
    loadAll();
  }

  // ── Adjust ───────────────────────────────────────────────────────
  async function handleAdjust(row) {
    if (!adjDelta || !adjReason) return;
    let delta = +adjDelta;
    if (adjMode === 'rusak')  delta = -Math.abs(delta);
    if (adjMode === 'masuk')  delta =  Math.abs(delta);
    const newQty = Math.max(0, row.qty + delta);
    const now = new Date().toISOString();
    await supabase.from('stock').update({ qty: newQty, updated_at: now }).eq('id', row._stockId);
    await supabase.from('stock_log').insert({
      menu_item_id:    row.menu_item_id,
      menu_variant_id: row.menu_variant_id || null,
      delta,
      reason:          adjReason,
      created_at:      now,
    });
    setAdjId(null); setAdjDelta(''); setAdjReason('');
    loadAll();
  }

  function startEdit(row) {
    setEditId(row._stockId);
    setEditVal({ qty: row.qty, low_threshold: row.low_threshold, unit: row.unit });
    setAdjId(null); setInitKey(null);
  }

  function viewLog(row) {
    setSelected(row);
    loadLogs(row);
  }

  const lowRows = stockRows.filter(s => s.qty <= s.low_threshold);

  function rowLabel(r) {
    return r.variantName ? `${r.itemEmoji} ${r.itemName} — ${r.variantName}` : `${r.itemEmoji} ${r.itemName}`;
  }

  function matchSearch(r) {
    if (!search) return true;
    const q = search.toLowerCase();
    return r.itemName.toLowerCase().includes(q) ||
           (r.itemCode || '').toLowerCase().includes(q) ||
           (r.variantName || '').toLowerCase().includes(q);
  }

  const filteredStocks = stockRows.filter(matchSearch);
  const filteredUninit = uninitialized.filter(matchSearch);

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Stok</div>
        <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>
          {stockRows.length} slot terlacak
          {uninitialized.length > 0 && <span style={{ color: '#E65100' }}> · {uninitialized.length} belum diinisialisasi</span>}
          {lowRows.length > 0 && <span style={{ color: '#C62828' }}> · {lowRows.length} stok rendah</span>}
        </div>
      </div>

      {lowRows.length > 0 && (
        <div style={{ background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '.78rem', color: '#C62828', fontWeight: 500 }}>⚠ Stok rendah:</span>
          {lowRows.map(s => (
            <span key={s._stockId} style={{ fontSize: '.76rem', color: '#C62828', background: 'rgba(198,40,40,.1)', borderRadius: 20, padding: '2px 8px' }}>
              {rowLabel(s)} ({s.qty} {s.unit})
            </span>
          ))}
        </div>
      )}

      <input
        placeholder="Cari nama/varian/kode..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ border: '1.5px solid #DDD', borderRadius: 8, padding: '7px 12px', fontSize: '.82rem', outline: 'none', marginBottom: 14, width: 240 }}
        onFocus={e => e.target.style.borderColor = 'var(--gold)'}
        onBlur={e => e.target.style.borderColor = '#DDD'}
      />

      <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow)', marginBottom: 24 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
          <thead>
            <tr>
              {['Item / Varian', 'Stok', 'Min', 'Satuan', 'Update', ''].map(h => (
                <th key={h} style={{ background: '#CD5226', color: '#fff', padding: '9px 14px', textAlign: 'left', fontSize: '.66rem', letterSpacing: '.8px', textTransform: 'uppercase', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredStocks.length === 0 && filteredUninit.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '24px 14px', textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem' }}>
                  Belum ada item. Tambahkan item di Menu POS terlebih dahulu.
                </td>
              </tr>
            )}

            {filteredStocks.map(row => {
              const isLow  = row.qty <= row.low_threshold;
              const isEdit = editId === row._stockId;
              const isAdj  = adjId  === row._stockId;
              return (
                <>
                  <tr key={row._stockId}
                    style={{ background: isLow ? '#FFF9F9' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = isLow ? '#FFF0F0' : '#FDFAF6'}
                    onMouseLeave={e => e.currentTarget.style.background = isLow ? '#FFF9F9' : 'transparent'}
                  >
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6' }}>
                      <span style={{ marginRight: 6 }}>{row.itemEmoji}</span>
                      <strong>{row.itemName}</strong>
                      {row.itemCode && <span style={{ marginLeft: 5, fontSize: '.72rem', color: 'var(--muted)' }}>{row.itemCode}</span>}
                      {row.variantName && (
                        <span style={{ marginLeft: 8, fontSize: '.74rem', background: '#EDE7F6', color: '#4527A0', borderRadius: 10, padding: '1px 7px' }}>
                          {row.variantName}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6' }}>
                      <strong style={{ color: isLow ? '#C62828' : 'inherit' }}>{row.qty}</strong>
                    </td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6', color: 'var(--muted)' }}>{row.low_threshold}</td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6', color: 'var(--muted)' }}>{row.unit}</td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6', fontSize: '.74rem', color: 'var(--muted)' }}>{fmtDate(row.updated_at)}</td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => { setAdjId(isAdj ? null : row._stockId); setEditId(null); setInitKey(null); setAdjMode('manual'); setAdjDelta(''); setAdjReason(''); }}
                          style={{ padding: '3px 8px', borderRadius: 6, background: isAdj ? '#CD5226' : 'var(--cream)', color: isAdj ? '#fff' : 'inherit', border: '1px solid var(--border)', fontSize: '.72rem', cursor: 'pointer' }}>
                          Adjust
                        </button>
                        <button onClick={() => startEdit(row)}
                          style={{ padding: '3px 8px', borderRadius: 6, background: isEdit ? '#CD5226' : 'var(--cream)', color: isEdit ? '#fff' : 'inherit', border: '1px solid var(--border)', fontSize: '.72rem', cursor: 'pointer' }}>
                          Set
                        </button>
                        <button onClick={() => viewLog(row)}
                          style={{ padding: '3px 8px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.72rem', cursor: 'pointer' }}>
                          Log
                        </button>
                      </div>
                    </td>
                  </tr>

                  {isEdit && (
                    <tr key={'edit-' + row._stockId}>
                      <td colSpan={6} style={{ padding: '10px 14px', background: '#F8F5F0', borderBottom: '1px solid #F5EEE6' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Qty:</label>
                          <input type="number" value={editVal.qty} onChange={e => setEditVal(v => ({ ...v, qty: +e.target.value }))}
                            style={{ width: 80, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none' }} />
                          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Min:</label>
                          <input type="number" value={editVal.low_threshold} onChange={e => setEditVal(v => ({ ...v, low_threshold: +e.target.value }))}
                            style={{ width: 60, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none' }} />
                          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Satuan:</label>
                          <input value={editVal.unit} onChange={e => setEditVal(v => ({ ...v, unit: e.target.value }))}
                            style={{ width: 80, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none' }} />
                          <button onClick={() => handleSetStock(row)}
                            style={{ padding: '5px 12px', borderRadius: 6, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.76rem', cursor: 'pointer' }}>
                            Simpan
                          </button>
                          <button onClick={() => setEditId(null)}
                            style={{ padding: '5px 10px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.76rem', cursor: 'pointer', color: 'var(--muted)' }}>
                            Batal
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {isAdj && (
                    <tr key={'adj-' + row._stockId}>
                      <td colSpan={6} style={{ padding: '10px 14px', background: '#F8F5F0', borderBottom: '1px solid #F5EEE6' }}>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                          {[['masuk', '📦 Barang Masuk', '#E8F5E9', '#1B5E20'], ['rusak', '🗑 Barang Rusak', '#FFEBEE', '#C62828'], ['manual', '✏️ Manual', '#F5F5F5', '#555']].map(([mode, label, bg, color]) => (
                            <button key={mode} type="button"
                              onClick={() => { setAdjMode(mode); setAdjDelta(''); setAdjReason(mode === 'masuk' ? 'Barang masuk' : mode === 'rusak' ? 'Barang rusak/kadaluarsa' : ''); }}
                              style={{ padding: '4px 12px', borderRadius: 20, border: '1px solid ' + color + '30', background: adjMode === mode ? bg : 'white', color: adjMode === mode ? color : 'var(--muted)', fontSize: '.74rem', cursor: 'pointer', fontWeight: adjMode === mode ? 500 : 400 }}>
                              {label}
                            </button>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>
                            {adjMode === 'masuk' ? 'Jumlah masuk:' : adjMode === 'rusak' ? 'Jumlah rusak:' : 'Delta (+ / -):'}
                          </label>
                          <input type="number" min={adjMode === 'rusak' ? 1 : undefined} value={adjDelta}
                            onChange={e => setAdjDelta(e.target.value)}
                            placeholder={adjMode === 'masuk' ? '+10' : adjMode === 'rusak' ? '3' : '-3 atau +10'}
                            style={{ width: 90, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none' }} />
                          <input value={adjReason} onChange={e => setAdjReason(e.target.value)}
                            placeholder={adjMode === 'masuk' ? 'Sumber (e.g. Restok dari supplier)' : adjMode === 'rusak' ? 'Keterangan kerusakan' : 'Alasan'}
                            style={{ flex: 1, minWidth: 160, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none' }} />
                          <button onClick={() => handleAdjust(row)}
                            style={{ padding: '5px 12px', borderRadius: 6, background: adjMode === 'rusak' ? '#C62828' : 'var(--dark)', color: 'white', border: 'none', fontSize: '.76rem', cursor: 'pointer' }}>
                            {adjMode === 'masuk' ? 'Catat Masuk' : adjMode === 'rusak' ? 'Catat Rusak' : 'Adjust'}
                          </button>
                          <button onClick={() => { setAdjId(null); setAdjMode('manual'); }}
                            style={{ padding: '5px 10px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.76rem', cursor: 'pointer', color: 'var(--muted)' }}>
                            Batal
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}

            {/* Belum diinisialisasi */}
            {filteredUninit.map(slot => {
              const isInit = initKey === slot.key;
              return (
                <>
                  <tr key={'uninit-' + slot.key}
                    style={{ background: '#FFFDE7' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FFF9C4'}
                    onMouseLeave={e => e.currentTarget.style.background = '#FFFDE7'}
                  >
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6' }}>
                      <span style={{ marginRight: 6 }}>{slot.itemEmoji}</span>
                      <strong>{slot.itemName}</strong>
                      {slot.itemCode && <span style={{ marginLeft: 5, fontSize: '.72rem', color: 'var(--muted)' }}>{slot.itemCode}</span>}
                      {slot.variantName && (
                        <span style={{ marginLeft: 8, fontSize: '.74rem', background: '#EDE7F6', color: '#4527A0', borderRadius: 10, padding: '1px 7px' }}>
                          {slot.variantName}
                        </span>
                      )}
                      <span style={{ marginLeft: 8, fontSize: '.68rem', background: '#FFF3CD', color: '#856404', borderRadius: 10, padding: '1px 7px', border: '1px solid #ffd34d' }}>
                        Belum ada stok
                      </span>
                    </td>
                    <td colSpan={4} style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6', color: 'var(--muted)', fontSize: '.8rem' }}>—</td>
                    <td style={{ padding: '9px 14px', borderBottom: '1px solid #F5EEE6' }}>
                      <button
                        onClick={() => { setInitKey(isInit ? null : slot.key); setInitVal({ qty: 0, low_threshold: 5, unit: 'porsi' }); setEditId(null); setAdjId(null); }}
                        style={{ padding: '3px 10px', borderRadius: 6, background: isInit ? '#CD5226' : '#FFF3CD', color: isInit ? '#fff' : '#856404', border: '1px solid #ffd34d', fontSize: '.72rem', cursor: 'pointer', fontWeight: 500 }}>
                        + Tambah Stok
                      </button>
                    </td>
                  </tr>

                  {isInit && (
                    <tr key={'init-' + slot.key}>
                      <td colSpan={6} style={{ padding: '10px 14px', background: '#FFFDE7', borderBottom: '1px solid #F5EEE6' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '.76rem', color: 'var(--muted)', fontWeight: 500 }}>
                            Inisialisasi: {slot.itemEmoji} {slot.itemName}{slot.variantName ? ` — ${slot.variantName}` : ''}
                          </span>
                          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Qty awal:</label>
                          <input type="number" min={0} value={initVal.qty} onChange={e => setInitVal(v => ({ ...v, qty: +e.target.value }))}
                            style={{ width: 80, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none' }} />
                          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Min:</label>
                          <input type="number" min={0} value={initVal.low_threshold} onChange={e => setInitVal(v => ({ ...v, low_threshold: +e.target.value }))}
                            style={{ width: 60, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none' }} />
                          <label style={{ fontSize: '.76rem', color: 'var(--muted)' }}>Satuan:</label>
                          <input value={initVal.unit} onChange={e => setInitVal(v => ({ ...v, unit: e.target.value }))}
                            style={{ width: 80, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none' }} />
                          <button onClick={() => handleInitStock(slot)}
                            style={{ padding: '5px 12px', borderRadius: 6, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.76rem', cursor: 'pointer', fontWeight: 500 }}>
                            Simpan
                          </button>
                          <button onClick={() => setInitKey(null)}
                            style={{ padding: '5px 10px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.76rem', cursor: 'pointer', color: 'var(--muted)' }}>
                            Batal
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Log panel */}
      {selected && (
        <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '18px 20px', boxShadow: 'var(--shadow)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.05rem' }}>
              Log: {rowLabel(selected)}
            </div>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
          </div>
          {logs.length === 0
            ? <div style={{ color: 'var(--muted)', fontSize: '.82rem' }}>Belum ada log.</div>
            : logs.map(l => (
              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F5EEE6', fontSize: '.8rem' }}>
                <span style={{ color: l.delta > 0 ? '#1B5E20' : '#C62828', fontFamily: "'DM Mono',monospace", minWidth: 50 }}>{l.delta > 0 ? '+' : ''}{l.delta}</span>
                <span style={{ flex: 1, color: 'var(--dark)', paddingLeft: 12 }}>{l.reason}</span>
                <span style={{ color: 'var(--muted)', fontSize: '.72rem' }}>{fmtDate(l.created_at)}</span>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}
